import React, { type PropsWithChildren, useRef } from 'react';
import { StyleSheet, TouchableOpacity, type TouchableOpacityProps, View } from 'react-native';

import { theme } from '@garnish/constants';

import { useHovered } from '../../../../../../../../hooks';
import { webOnlyStyles } from '../../../../../../../../utils';
import { BodyText } from '../../../../../../../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingCurrentOrderLinkPressable = (props: Props) => {
  const { children, ...restProps } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const linkRef = useRef<View>(null);
  const isHovered = useHovered(linkRef);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const contentStyles = [contentWebStyles, isHovered ? styles.contentHovered : undefined];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity
      {...restProps}
      accessibilityRole="link"
      activeOpacity={LINK_ACTIVE_OPACITY}
      style={styles.link}
    >
      <View ref={linkRef} style={contentStyles}>
        <BodyText
          maxFontSizeMultiplier={1.2}
          numberOfLines={1}
          sizeMatch={['16']}
          style={styles.label}
        >
          {children}
        </BodyText>
      </View>
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LINK_ACTIVE_OPACITY = 0.7;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  link: {
    flexShrink: 0,
  },
  contentHovered: {
    opacity: LINK_ACTIVE_OPACITY,
  },
  label: {
    color: theme.colors.WHITE,
    textDecorationLine: 'underline',
  },
});

const contentWebStyles = webOnlyStyles({
  transition: `opacity ${theme.transitions.base}ms`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<Pick<TouchableOpacityProps, 'aria-label' | 'onPress'>>;
